import React from 'react';
import { ArrowRight, MapPin, Settings, Palette, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center pt-24 overflow-hidden bg-black"
      data-testid="hero-section"
    >
      {/* Background - no image on mobile */}
      <div className="absolute inset-0 z-0">
        <div className="hidden lg:block absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_service-devis-1/artifacts/hsrxlrrm_IMG_4054.jpeg"
            alt="Background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8 text-center lg:text-left">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 border border-[#C9A55C]/40 px-5 py-2 animate-fade-up">
                <MapPin className="w-4 h-4 text-[#C9A55C]" />
                <span className="text-sm tracking-widest uppercase text-[#C9A55C]">Hauts-de-France</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-bold tracking-tight leading-none animate-fade-up animation-delay-100">
                <span className="text-white">HELP</span>
                <span className="text-[#C9A55C]">ADN</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-[#C9A55C] font-light tracking-wide animate-fade-up animation-delay-200">
                Consultante IA & Automatisation + Product Buildeuse
              </p>
              
              <p className="text-lg text-white/60 max-w-xl mx-auto lg:mx-0 font-light leading-relaxed animate-fade-up animation-delay-300">
                Je crée des outils IA, j'automatise vos processus administratifs et je structure vos workflows 
                pour simplifier vos démarches, optimiser votre organisation et rendre votre quotidien plus intelligent.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start animate-fade-up animation-delay-400">
              <Button
                onClick={(e) => scrollToSection(e, '#pricing')}
                className="btn-gold rounded-none h-14 px-10 text-sm font-semibold tracking-wider uppercase"
                data-testid="hero-cta-services"
              >
                Nos services
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="rounded-none h-14 px-10 text-sm font-semibold tracking-wider uppercase border-[#C9A55C]/50 text-[#C9A55C] hover:bg-[#C9A55C]/10 hover:border-[#C9A55C]"
                data-testid="hero-cta-contact"
              >
                Demander un devis
              </Button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-12 pt-4 justify-center lg:justify-start animate-fade-up animation-delay-400">
              <div className="text-center">
                <p className="text-4xl font-serif font-bold text-[#C9A55C]">100+</p>
                <p className="text-sm text-white/50 tracking-wider uppercase mt-1">Clients</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-serif font-bold text-[#C9A55C]">98%</p>
                <p className="text-sm text-white/50 tracking-wider uppercase mt-1">Satisfaction</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-serif font-bold text-[#C9A55C]">2h</p>
                <p className="text-sm text-white/50 tracking-wider uppercase mt-1">Réponse</p>
              </div>
            </div>
          </div>

          {/* Right Content - Image (hidden on mobile) */}
          <div className="hidden lg:flex justify-center items-center">
            <div className="relative">
              <div className="relative overflow-hidden gold-glow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_service-devis-1/artifacts/hsrxlrrm_IMG_4054.jpeg"
                  alt="Jennifer - HELP ADN"
                  className="w-[420px] h-[520px] object-cover object-center"
                />
                <div className="absolute inset-0 border border-[#C9A55C]/40" />
              </div>
              {/* Corner accents */}
              <div className="absolute -top-4 -left-4 w-10 h-10 border-t-2 border-l-2 border-[#C9A55C]" />
              <div className="absolute -top-4 -right-4 w-10 h-10 border-t-2 border-r-2 border-[#C9A55C]" />
              <div className="absolute -bottom-4 -left-4 w-10 h-10 border-b-2 border-l-2 border-[#C9A55C]" />
              <div className="absolute -bottom-4 -right-4 w-10 h-10 border-b-2 border-r-2 border-[#C9A55C]" />
            </div>
          </div>
        </div>

        {/* Services Cards below hero */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Automatisations Intelligentes */}
          <div className="bg-black/50 border border-[#C9A55C]/30 p-8 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-8 h-8 text-[#C9A55C]" />
              <h3 className="text-xl font-serif font-bold text-white">Automatisations Intelligentes</h3>
            </div>
            <p className="text-white/60 mb-6 text-sm leading-relaxed">
              Workflows automatisés sur mesure. Génération de documents, traitement de données, 
              relances programmées. Vous gagnez du temps, l'IA travaille pour vous.
            </p>
            <div className="space-y-2">
              {[
                'Génération automatique de documents administratifs',
                'Réponses instantanées aux courriers officiels',
                'Classement intelligent de vos fichiers',
                'Extraction de données depuis vos PDF',
                'Dossiers créés sans intervention manuelle',
                'Relances programmées (factures, justificatifs)',
                'Agents IA pour vos tâches répétitives'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2 text-sm text-white/70">
                  <CheckCircle className="w-4 h-4 text-[#C9A55C] flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Créations Digitales */}
          <div className="bg-black/50 border border-[#C9A55C]/30 p-8 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <Palette className="w-8 h-8 text-[#C9A55C]" />
              <h3 className="text-xl font-serif font-bold text-white">Créations Digitales</h3>
            </div>
            <p className="text-white/60 mb-6 text-sm leading-relaxed">
              Outils IA sur mesure, agents intelligents, interfaces intuitives. 
              Des solutions prêtes à l'emploi pour transformer votre organisation.
            </p>
            <div className="space-y-2">
              {[
                'Agents IA professionnels',
                'Chatbots administratifs',
                'Générateurs de documents',
                'Mini-applications IA',
                'Workflows automatisés Make/Zapier/n8n',
                'Interfaces connectées (API, webhooks)'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2 text-sm text-white/70">
                  <CheckCircle className="w-4 h-4 text-[#C9A55C] flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A55C]/50 to-transparent" />
    </section>
  );
};

export default HeroSection;
